import { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { cn } from '@/lib/utils';

const STEPS = [
  { num: 1, optional: false },
  { num: 2, optional: true },
  { num: 3, optional: true },
  { num: 4, optional: true },
] as const;

interface WizardStepIndicatorProps {
  current: number;
}

export function WizardStepIndicator({ current }: WizardStepIndicatorProps) {
  const stepLabels: Record<number, ReactNode> = {
    1: <Trans>Supply point</Trans>,
    2: <Trans>Access</Trans>,
    3: <Trans>Devices</Trans>,
    4: <Trans>Products</Trans>,
  };

  return (
    <div className="flex items-start">
      {STEPS.map((step, i) => {
        const isComplete = current > step.num;
        const isActive = current === step.num;
        return (
          <div key={step.num} className="flex items-start">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'flex size-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors',
                  isComplete
                    ? 'border-primary bg-primary text-primary-foreground'
                    : isActive
                      ? 'border-primary bg-background text-primary'
                      : 'border-border bg-background text-muted-foreground'
                )}
              >
                {isComplete ? <Check size={14} /> : step.num}
              </div>
              <div className="mt-1.5 text-center">
                <div
                  className={cn(
                    'text-[11px] font-semibold',
                    isActive
                      ? 'text-foreground'
                      : isComplete
                        ? 'text-primary'
                        : 'text-muted-foreground'
                  )}
                >
                  {stepLabels[step.num]}
                </div>
                {step.optional && (
                  <div className="text-[10px] text-muted-foreground">
                    <Trans>optional</Trans>
                  </div>
                )}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  'mt-4 mx-2 h-0.5 w-12 shrink-0 transition-colors',
                  current > step.num ? 'bg-primary' : 'bg-border'
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
